import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from "@angular/core";
import {
  DropDownListComponent,
  FilteringEventArgs,
} from "@syncfusion/ej2-angular-dropdowns";
import { Query } from "@syncfusion/ej2-data";
import moment from "moment";
import {
  EmailPattern,
  FieldItem,
  filterShowSection,
  SectionItem,
} from "./constants";

const FORM_PROXY_TAG = Symbol.for("form-proxy");

@Component({
  selector: "rs-drawer-form",
  templateUrl: "./drawer-form.component.html",
  styleUrls: ["./drawer-form.component.scss"],
})
export class DrawerFormComponent implements OnInit, OnChanges, AfterViewInit {
  @Input("form") _form: Record<string, any> = {};
  @Input("sections") _sections: SectionItem[] = [];
  @Input("optionsMap") optionsMap: Record<string, any[]> = {};
  @Input("disabled") formDisabled = false;

  @Input("showAnchor") showAnchor = true;
  @Input("customTemplate") customTemplate: TemplateRef<any>;
  @Input("customSectionTemplate")
  customSectionTemplate: Record<string, TemplateRef<any>>;

  @Input("optionFields") optionFields = {
    text: "text",
    value: "code",
    disabled: "disabled",
    groupBy: null,
  };
  @Input("showFilterNumber") showFilterNumber = 10;
  @Input("numberFormat") numberFormat: string;
  @Input("dateFormat") dateFormat = "yyyy-MM-dd";

  @Output("formChange") formChange = new EventEmitter<Record<string, any>>();
  @Output("fieldChange") fieldChange = new EventEmitter<{
    field: FieldItem;
    value: any;
  }>();

  @ViewChild("drawerFormEl", { static: false })
  rootEl: ElementRef<HTMLDivElement>;

  // 自定义 field 模板和自定义 section 模板的容器
  @ViewChildren("customTemplateContainer", { read: ViewContainerRef })
  private containers: QueryList<ViewContainerRef>;
  @ViewChildren("customSectionContainer", { read: ViewContainerRef })
  private sectionContainers: QueryList<ViewContainerRef>;

  @ViewChildren("dropdown")
  dropdowns: QueryList<DropDownListComponent>;

  sections: SectionItem[] = [];
  fieldTypeMap: Partial<Record<FieldItem["fieldFormType"], FieldItem[]>> = {};
  formFieldMap: Record<string, FieldItem> = {};

  form: Record<string, any> = {};
  fieldValidMap: Record<string, boolean> = {};

  // 存储 datepicker 的值
  dateForm: Record<string, Date> = {};
  dateValidMap: Record<string, boolean> = {};

  constructor(private ref: ChangeDetectorRef) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes._sections) {
      this.sections = filterShowSection(changes._sections.currentValue);
      this.formatFieldMap();
      this.setForm({ ...this.form, ...this._form });
    }
    if (changes._form) {
      if (!changes._form.currentValue[FORM_PROXY_TAG]) {
        this.setForm(changes._form.currentValue);
      } else {
        this.form = changes._form.currentValue;
      }
      if (!changes._form.firstChange) {
        this.checkFormChange(
          changes._form.previousValue,
          changes._form.currentValue
        );
      }
    }
  }

  ngAfterViewInit() {
    this.customTemplateRender(this.containers);
    this.customSectionRender(this.sectionContainers);
    this.containers.changes.subscribe(
      (containers: QueryList<ViewContainerRef>) => {
        this.containers = containers;
        this.customTemplateRender(containers);
      }
    );
    this.sectionContainers.changes.subscribe(
      (containers: QueryList<ViewContainerRef>) => {
        this.sectionContainers = containers;
        this.customSectionRender(containers);
      }
    );
    this.dropdowns.changes.subscribe(
      (dropdowns: QueryList<DropDownListComponent>) => {
        this.dropdowns = dropdowns;
      }
    );
    if (this.showAnchor) {
      this.initAnchorScrollHandler();
    }
  }

  customTemplateRender(containers: QueryList<ViewContainerRef>) {
    if (this.customTemplate && containers.length > 0) {
      const customFields = this.fieldTypeMap.Custom || [];
      containers.forEach((container, index) => {
        container.clear();
        container.createEmbeddedView(this.customTemplate, {
          $implicit: customFields[index],
        });
      });
    }
  }

  customSectionRender(containers: QueryList<ViewContainerRef>) {
    if (this.customSectionTemplate && containers.length > 0) {
      const customSections = this.sections.filter(
        (section) => section.customKey
      );
      containers.forEach((container, index) => {
        container.clear();
        const customKey = customSections[index].customKey;
        container.createEmbeddedView(this.customSectionTemplate[customKey], {
          $implicit: customSections[index],
        });
      });
    }
  }

  formatFieldMap() {
    this.fieldTypeMap = {};
    this.formFieldMap = {};
    this.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (!this.fieldTypeMap[field.fieldFormType]) {
          this.fieldTypeMap[field.fieldFormType] = [];
        }
        this.fieldTypeMap[field.fieldFormType].push(field);
        this.formFieldMap[field.formKey] = field;
      });
    });
  }

  setForm(form: Record<string, any>) {
    this.form = this.getFormProxy(this.formatForm(this.sections, form));
    this.syncFormField();
    this.formChange.emit(this.form);
  }

  equal(value: any, compareValue: any) {
    // 只对数组进行特殊判断
    if (Array.isArray(value) && Array.isArray(compareValue)) {
      return (
        value.length === compareValue.length &&
        value.every((i, index) => i === compareValue[index])
      );
    }
    return Object.is(value, compareValue);
  }

  checkFormChange(previous: Record<string, any>, current: Record<string, any>) {
    Object.entries(current).forEach(([key, currentValue]) => {
      if (!this.equal(previous[key], currentValue)) {
        const field = this.getField(key);
        if (field) {
          this.formItemValidator(field);
        }
      }
    });
  }

  syncFormToDateForm(field: FieldItem, value: any) {
    if (value) {
      const date = moment(value);
      this.dateForm[field.formKey] = date.isValid() ? date.toDate() : null;
    } else {
      this.dateForm[field.formKey] = null;
    }
  }

  syncFormField() {
    this.sections.forEach((section: SectionItem) => {
      section.fields.forEach((field) => {
        switch (field.fieldFormType) {
          case "Datepicker":
            this.syncFormToDateForm(field, this.form[field.formKey]);
            break;
        }
      });
    });
  }

  formatForm(sections: SectionItem[], form: Record<string, any>) {
    sections.forEach((section: SectionItem) => {
      section.fields.forEach((field) => {
        switch (field.fieldFormType) {
          case "Text":
          case "Email":
          case "Textarea":
            if (form[field.formKey] === undefined) {
              form[field.formKey] = "";
            }
            break;

          case "Tags":
          case "Checkbox":
          case "MultiSelect":
            if (!Array.isArray(form[field.formKey])) {
              form[field.formKey] = [];
            }
            break;

          case "Switch":
            form[field.formKey] = !!form[field.formKey];
            break;

          default:
            if (form[field.formKey] === undefined) {
              form[field.formKey] = null;
            }
        }
      });
    });
    return form;
  }

  getFormProxy(form: Record<string, any>) {
    // 现在不支持嵌套 form 属性，只对浅层进行处理
    const proxyForm = new Proxy(form, {
      set: (target, prop: string, value: any) => {
        target[prop] = value;

        const field = this.getField(prop);
        if (field) {
          if (field.fieldFormType === "Datepicker") {
            this.syncFormToDateForm(field, value);
          }
          this.formItemValidator(field);
        }

        return true;
      },
    });

    Object.defineProperty(proxyForm, FORM_PROXY_TAG, {
      value: true,
      writable: false,
      enumerable: false,
      configurable: false,
    });
    return proxyForm;
  }

  // 表单提交前会触发这个函数
  updateForm(value: any, field: FieldItem) {
    this.form[field.formKey] = value;
    this.formChange.emit(this.form);
    this.fieldChange.emit({ field, value });
  }

  formItemValidator(field: FieldItem, isAuto = false) {
    const value = this.form[field.formKey];
    let isValid = true;
    if (field.required) {
      isValid = Array.isArray(value)
        ? value.length !== 0
        : ![null, undefined, ""].includes(value);
    }
    if (isValid && field.fieldFormType === "Email" && value) {
      isValid = EmailPattern.test(value);
    }
    if (isValid && field.maxlength) {
      isValid = String(value).length <= field.maxlength;
    }
    if (isValid && field.minlength) {
      isValid = String(value).length >= field.minlength;
    }
    if (isValid && field.max) {
      isValid = value <= field.max;
    }
    if (isValid && field.min) {
      isValid = value >= field.min;
    }
    if (isValid && typeof field.validator === "function") {
      isValid = field.validator.call(null, value, field);
    }
    if (!isAuto) {
      this.fieldValidMap[field.formKey] = isValid;
    }
    return isValid;
  }

  preCheckFormIsValid() {
    let formIsValid = true;
    this.sections.forEach((section) =>
      section.fields.forEach((field) => {
        formIsValid = this.formItemValidator(field) && formIsValid;
      })
    );
    return formIsValid;
  }

  get formIsValid() {
    return this.sections.every((section) =>
      section.fields.every((field) => this.formItemValidator(field, true))
    );
  }

  getField(formKey: string) {
    return this.formFieldMap[formKey] || null;
  }

  private callFunction(fn: Function, ...args: any[]) {
    this.formItemValidator(args[1] || args[0]);
    if (typeof fn === "function") {
      fn.apply(null, args);
    }
  }

  onInput(value: string, field: FieldItem) {
    this.callFunction(field.onInput, value, field);
  }

  onComponentChange(event: any, field: FieldItem) {
    if (!event.isInteracted) {
      return;
    }
    if (field.fieldFormType !== "Datepicker") {
      this.onChange(event.value, field);
      return;
    }
    console.log("change", event);
    const value = event.value ? moment(event.value).format("YYYY-MM-DD") : null;
    this.dateForm[field.formKey] = event.value;
    this.onChange(value, field);
  }

  onChange(value: any, field: FieldItem) {
    this.updateForm(value, field);
    this.callFunction(field.onChange, value, field);
  }

  onDatePickerBlur(event: any, field: FieldItem) {
    this.onBlur(field);

    setTimeout(() => {
      const $input = event.model.element.querySelector("input");
      if ($input && $input.value && !this.dateForm[field.formKey]) {
        this.dateValidMap[field.formKey] = false;
      } else {
        delete this.dateValidMap[field.formKey];
      }
    }, 50);
  }

  onFocus(field: FieldItem) {
    this.callFunction(field.onFocus, field);
  }

  onBlur(field: FieldItem) {
    this.callFunction(field.onBlur, field);
  }

  onFiltering(event: FilteringEventArgs, field: FieldItem) {
    const query = new Query();
    if (event.text) {
      query.where(this.optionFields.text, "contains", event.text, true);
    }
    event.updateData(this.getOptions(field), query);
  }

  onDropdownDataBound(event: any, field: FieldItem) {
    const index = this.fieldTypeMap[field.fieldFormType].indexOf(field);
    const component = this.dropdowns.toArray()[index];
    if (event.items.length > 0 && index !== -1 && component) {
      component.value = this.form[field.formKey];
    }
  }

  getOptions(field: FieldItem) {
    if (Array.isArray(this.optionsMap[field.optionKey])) {
      return this.optionsMap[field.optionKey];
    }
    if (Array.isArray(this.optionsMap[field.formKey])) {
      return this.optionsMap[field.formKey];
    }
    return [];
  }

  getDisabled(field: FieldItem) {
    if (this.formDisabled) {
      return true;
    }
    if (typeof field.disabled === "function") {
      return field.disabled();
    }
    return field.disabled;
  }

  getOptionFields(field: FieldItem) {
    if (["Dropdown", "AutoComplete"].includes(field.fieldFormType)) {
      return { ...this.optionFields, groupBy: field.groupKey || null };
    }
    return this.optionFields;
  }

  @ViewChildren("sectionEl")
  sectionEls: QueryList<ElementRef<HTMLDivElement>>;
  scrollIndex = 0;
  isClickAnchor = false;

  onClickAnchor(index: number) {
    const sectionItems = this.sectionEls.toArray();
    sectionItems[index].nativeElement.scrollIntoView(true);
    this.scrollIndex = index;
    this.isClickAnchor = true;
  }

  findScrollViewEl(el: HTMLElement): HTMLElement {
    if (el === document.body) {
      return el;
    }
    if (el.scrollHeight && el.scrollHeight !== el.offsetHeight) {
      return el;
    }
    return this.findScrollViewEl(el.parentElement);
  }

  initAnchorScrollHandler() {
    const scrollViewEl = this.findScrollViewEl(this.rootEl.nativeElement);
    const scrollHeight = scrollViewEl.scrollHeight - scrollViewEl.offsetHeight;
    const sectionViewRatio = this.sectionEls.toArray().map((section) => {
      const ratio =
        section.nativeElement.offsetHeight / scrollViewEl.scrollHeight;
      return scrollHeight * ratio;
    });

    const onScroll = () => {
      if (this.isClickAnchor) {
        this.isClickAnchor = false;
        return;
      }
      const scrollTop =
        scrollViewEl.scrollTop > scrollHeight
          ? scrollHeight
          : scrollViewEl.scrollTop;
      let radioScrollHeight = 0;
      for (let index = 0; index < sectionViewRatio.length; index++) {
        radioScrollHeight += sectionViewRatio[index];
        if (radioScrollHeight >= scrollTop) {
          this.scrollIndex = index;
          break;
        }
      }
      while (
        this.sections[this.scrollIndex] &&
        !this.sections[this.scrollIndex].title
      ) {
        this.scrollIndex++;
      }
      this.ref.markForCheck();
    };

    onScroll();
    scrollViewEl.addEventListener("scroll", onScroll);
  }
}
